/** Window mechanics: open/close, click-outside, drag by header, resize, maximize. */

import { hideCallout } from "./callout";
import { MIN_PANEL_HEIGHT, MIN_PANEL_WIDTH } from "./config";
import { ICON, type WidgetElements } from "./dom";

export function bindWindow(ui: WidgetElements, callout: HTMLElement, onOpen: () => void): void {
  // A drag or resize ends with a click; this stops that click closing the panel.
  let suppressOutsideClick = false;
  const suppressBriefly = () => {
    suppressOutsideClick = true;
    window.setTimeout(() => (suppressOutsideClick = false), 100);
  };

  const setOpen = (open: boolean) => {
    ui.panel.classList.toggle("open", open);
    ui.button.setAttribute("aria-expanded", String(open));
    ui.panel.setAttribute("aria-hidden", String(!open));
    ui.panelContainer.style.pointerEvents = open ? "auto" : "none";
  };

  ui.button.addEventListener("click", () => {
    const open = !ui.panel.classList.contains("open");
    setOpen(open);
    if (open) {
      hideCallout(callout);
      ui.input.focus();
      onOpen();
    }
  });
  ui.closeBtn.addEventListener("click", () => setOpen(false));

  document.addEventListener("click", (event) => {
    const target = event.target as Node;
    if (
      !suppressOutsideClick &&
      ui.panel.classList.contains("open") &&
      !ui.buttonContainer.contains(target) &&
      !ui.panelContainer.contains(target)
    ) {
      setOpen(false);
    }
  });

  let maximized = false;
  ui.maximizeBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    maximized = !maximized;
    ui.panel.classList.toggle("maximized", maximized);
    ui.maximizeBtn.setAttribute("aria-label", maximized ? "Minimize Chat Window" : "Maximize Chat Window");
    ui.maximizeBtn.setAttribute("data-tooltip", maximized ? "Minimize" : "Maximize");
    ui.maximizeBtn.innerHTML = maximized ? ICON.minimize : ICON.maximize;
  });

  bindDrag(ui, suppressBriefly);
  bindResize(ui, suppressBriefly);
}

/** Pointer-driven drag so it works for mouse, pen and touch alike. */
function bindDrag(ui: WidgetElements, onEnd: () => void): void {
  const container = ui.panelContainer;
  ui.header.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest("button") || !ui.panel.classList.contains("open")) return;
    e.preventDefault();
    const rect = container.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    Object.assign(container.style, {
      right: "auto",
      bottom: "auto",
      left: `${rect.left}px`,
      top: `${rect.top}px`,
    });
    ui.header.style.cursor = "grabbing";
    document.body.style.userSelect = "none";

    const move = (ev: PointerEvent) => {
      const maxLeft = window.innerWidth - container.offsetWidth;
      const maxTop = window.innerHeight - container.offsetHeight;
      const minTop = -(ui.header.offsetHeight - 20); // header may tuck slightly off-screen
      container.style.left = `${Math.min(Math.max(ev.clientX - offsetX, 0), maxLeft)}px`;
      container.style.top = `${Math.min(Math.max(ev.clientY - offsetY, minTop), maxTop)}px`;
    };
    const up = () => {
      ui.header.style.cursor = "grab";
      document.body.style.userSelect = "";
      document.removeEventListener("pointermove", move);
      onEnd();
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up, { once: true });
  });
}

function bindResize(ui: WidgetElements, onEnd: () => void): void {
  ui.resizeHandle.addEventListener("pointerdown", (e) => {
    if (!ui.panel.classList.contains("open")) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = ui.panel.offsetWidth;
    const startHeight = ui.panel.offsetHeight;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "nwse-resize";

    const move = (ev: PointerEvent) => {
      ui.panel.style.width = `${Math.max(MIN_PANEL_WIDTH, startWidth + ev.clientX - startX)}px`;
      ui.panel.style.height = `${Math.max(MIN_PANEL_HEIGHT, startHeight + ev.clientY - startY)}px`;
    };
    const up = () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      document.removeEventListener("pointermove", move);
      onEnd();
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up, { once: true });
  });
}
